import dayjs from "dayjs";
import { getAllInvoice, updateInvoice } from "./API";

export const INVOICE_AUTO_DRAFT_PENDING_KEY = "invoiceAutoDraftPending";
export const INVOICE_AUTO_DRAFT_COMPLETED_KEY = "invoiceAutoDraftCompletedAt";

export const markInvoiceAutoDraftPending = () => {
  sessionStorage.setItem(INVOICE_AUTO_DRAFT_PENDING_KEY, "true");
};

export const markInvoiceAutoDraftCompleted = () => {
  sessionStorage.removeItem(INVOICE_AUTO_DRAFT_PENDING_KEY);
  sessionStorage.setItem(
    INVOICE_AUTO_DRAFT_COMPLETED_KEY,
    Date.now().toString()
  );
};

export const clearInvoiceAutoDraftPending = () => {
  sessionStorage.removeItem(INVOICE_AUTO_DRAFT_PENDING_KEY);
};

export const clearInvoiceAutoDraftCompleted = () => {
  sessionStorage.removeItem(INVOICE_AUTO_DRAFT_COMPLETED_KEY);
};

export const hasInvoiceAutoDraftActivity = () =>
  Boolean(
    sessionStorage.getItem(INVOICE_AUTO_DRAFT_PENDING_KEY) ||
      sessionStorage.getItem(INVOICE_AUTO_DRAFT_COMPLETED_KEY)
  );

const PREFIX = "INV";

export const formatInvoiceSequence = (num: number) =>
  num.toString().padStart(4, "0");

export const generateInvoiceNumber = (seq: number, year = dayjs().format("YYYY")) =>
  `${PREFIX}-${year}-${formatInvoiceSequence(seq)}`;

export const getInvoiceSequence = (
  invoiceNumber?: string,
  year = dayjs().format("YYYY")
) => {
  if (!invoiceNumber) return null;

  const parts = invoiceNumber.split("-");
  if (parts.length !== 3 || parts[0] !== PREFIX || parts[1] !== year) {
    return null;
  }

  const seq = parseInt(parts[2], 10);
  return Number.isNaN(seq) ? null : seq;
};

const getInvoiceList = async () => {
  const response: any = await getAllInvoice(1, true);
  return response?.data?.data || response?.data || response || [];
};

const getNextFinalizedSequence = (invoices: any[], year = dayjs().format("YYYY")) => {
  const finalizedSequences = invoices
    .filter((invoice: any) => (invoice?.invoice_status || "drafted") !== "drafted")
    .map((invoice: any) => getInvoiceSequence(invoice?.invoice_number, year))
    .filter((seq: number | null) => seq !== null) as number[];

  if (!finalizedSequences.length) {
    return 1;
  }

  return Math.max(...finalizedSequences) + 1;
};

const getNextDraftSequence = (invoices: any[], year = dayjs().format("YYYY")) => {
  const allSequences = invoices
    .map((invoice: any) => getInvoiceSequence(invoice?.invoice_number, year))
    .filter((seq: number | null) => seq !== null) as number[];

  if (!allSequences.length) {
    return 1;
  }

  return Math.max(...allSequences) + 1;
};

const shiftDraftInvoiceNumbers = async (
  fromSequence: number,
  invoices: any[],
  year = dayjs().format("YYYY")
) => {
  const draftInvoices = invoices
    .filter((invoice: any) => (invoice?.invoice_status || "drafted") === "drafted")
    .map((invoice: any) => ({
      ...invoice,
      sequence: getInvoiceSequence(invoice?.invoice_number, year),
    }))
    .filter((invoice: any) => invoice.sequence !== null && invoice.sequence >= fromSequence)
    .sort((a: any, b: any) => b.sequence - a.sequence);

  for (const draftInvoice of draftInvoices) {
    await updateInvoice(draftInvoice._id, {
      invoice_number: generateInvoiceNumber(draftInvoice.sequence + 1, year),
    });
  }
};
export const buildDraftDisplayNumberMap = (
  invoices: any[],
  year = dayjs().format("YYYY")
) =>
  new Map(
    invoices
      .filter((invoice: any) => (invoice?.invoice_status || "drafted") === "drafted")
      .map((invoice: any) => ({
        ...invoice,
        sequence: getInvoiceSequence(invoice?.invoice_number, year),
      }))
      .filter((invoice: any) => invoice.sequence !== null)
      .sort((a: any, b: any) => {
        if (a.sequence !== b.sequence) {
          return a.sequence - b.sequence;
        }

        const aTime = new Date(a.createdAt || 0).getTime();
        const bTime = new Date(b.createdAt || 0).getTime();
        if (aTime !== bTime) {
          return aTime - bTime;
        }

        return String(a._id || "").localeCompare(String(b._id || ""));
      })
      .map((invoice: any, index: number) => [invoice._id, index + 1] as const)
  );

export const getDraftDisplaySequenceForInvoice = async (invoiceId?: string) => {
  if (!invoiceId) return null;

  const year = dayjs().format("YYYY");
  const invoices = await getInvoiceList();
  const draftDisplayNumberMap = buildDraftDisplayNumberMap(invoices, year);

  return draftDisplayNumberMap.get(invoiceId) ?? null;
};



export const getNextSavedInvoicePreviewSequence = async () => {
  const year = dayjs().format("YYYY");
  const invoices = await getInvoiceList();
  return getNextFinalizedSequence(invoices, year);
};

export const resolveInvoiceNumberForCreate = async (invoiceStatus: "sent" | "drafted") => {
  const year = dayjs().format("YYYY");
  const invoices = await getInvoiceList();

  if (invoiceStatus === "drafted") {
    const nextSequence = getNextDraftSequence(invoices, year);
    return {
      invoiceNumber: generateInvoiceNumber(nextSequence, year),
      sequence: nextSequence,
    };
  }

  const nextSequence = getNextFinalizedSequence(invoices, year);
  await shiftDraftInvoiceNumbers(nextSequence, invoices, year);

  return {
    invoiceNumber: generateInvoiceNumber(nextSequence, year),
    sequence: nextSequence,
  };
};




